import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, User, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(form.username, form.password);
      toast.success("Berhasil masuk. Selamat datang!");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Gagal masuk. Periksa kembali kredensial Anda.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-navy relative overflow-hidden flex-col justify-between p-12 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #DC2626 0, transparent 40%), radial-gradient(circle at 80% 80%, #1E3E6B 0, transparent 45%)" }} />
        <div className="relative flex items-center gap-3">
          <img src="/assets/logo-bawaslu-full.png" alt="Logo Bawaslu Provinsi Sulawesi Tenggara" className="h-12 w-auto" />
        </div>
        <div className="relative flex items-center gap-3 mt-3">
          <div><div className="font-extrabold text-lg">HARMONI BAWASLU</div><div className="text-xs text-slate-300">Admin Panel</div></div>
        </div>
        <div className="relative">
          <ShieldCheck className="h-12 w-12 text-brandred mb-4" />
          <h2 className="text-3xl font-extrabold leading-tight">Satu Pintu Komunikasi Publik Bawaslu</h2>
          <p className="text-slate-300 mt-3 max-w-md">Dashboard pengelolaan informasi, aspirasi masyarakat, media, dan monitoring Humas Bawaslu Provinsi Sulawesi Tenggara.</p>
        </div>
        <div className="relative text-xs text-slate-400">© 2026 Bawaslu Provinsi Sulawesi Tenggara</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-surface">
        <div className="w-full max-w-sm">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brandred mb-8">
            <ArrowLeft className="h-4 w-4" /> Kembali ke Beranda
          </button>
          <h1 className="text-2xl font-extrabold text-navy">Masuk Admin</h1>
          <p className="text-slate-500 text-sm mt-1 mb-8">Silakan masuk untuk mengakses dashboard.</p>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label>Username</Label>
              <div className="relative mt-1.5">
                <User className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="login-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="admin" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label>Password</Label>
              <div className="relative mt-1.5">
                <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="pl-9 h-11" />
              </div>
            </div>
            {error && <p className="text-sm text-brandred bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-brandred hover:bg-brandred-700 text-white font-semibold">
              {loading ? "Memproses…" : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
